import type { Nullish } from '../../types/utility';
interface Options {
  outputCase?: 'sentence' | 'title' | 'pascal' | 'camel' | 'snake' | 'kebab' | 'upper' | 'lower';
  normalize?: 'whitespace' | 'naming';
}

type AcronymKeys = Exclude<keyof typeof ACRONYMS, 'normalize'>;

const CAMEL_PATTERN = /([a-z])([A-Z])/g;
const CAPITAL_LETTER_PATTERN = /^[A-Z]/;
const PASCAL_PATTERN = /(?=[A-Z])/g;
const WHITESPACE_PATTERN = /\s+/g;
const SNAKE_KEBAB_PATTERN = /[_-]+/g;
const TRAILING_PUNCTUATION_PATTERN = /[^\w\s]+$/g;

const MINOR_WORDS = new Set([
  'a',
  'an',
  'the',
  'and',
  'but',
  'or',
  'in',
  'on',
  'at',
  'to',
  'from',
  'with',
  'by',
  'for',
  'of',
]);

const JOINERS = {
  kebab: '-',
  snake: '_',
};

const ACRONYMS = {
  ai: 'AI', // Artificial Intelligence
  aol: 'AOL', // Agent Operations Lead
  bpo: 'BPO', // Broker Price Opinion
  cma: 'CMA', // Competitive Market Analysis
  fha: 'FHA', // Federal Housing Administration
  hoa: 'HOA', // Homeowners Association
  lpr: 'LPR', // Listing Price Report
  lss: 'LSS', // Listing Sales Specialist
  qc: 'QC', // Quality Control
  tss: 'TSS', // Transaction Support Specialist
  va: 'VA', // Veterans Affairs
  normalize: function (key: string) {
    // replace an acronym with trailing punctuation e.g. lss, -> lss -> LSS -> LSS,
    const normalizedKey = key.replace(TRAILING_PUNCTUATION_PATTERN, '');
    const acronym = this[normalizedKey as AcronymKeys];

    if (acronym) {
      return key.replace(normalizedKey, acronym);
    }

    return;
  },
};

export function formatString(value: string | Nullish, options: Options = {}) {
  if (!value) {
    return '';
  }

  const { outputCase, normalize } = options;
  let output = value;

  if (normalize === 'whitespace') {
    output = value.trim().replace(WHITESPACE_PATTERN, ' ');
  } else if (normalize === 'naming') {
    // ensure all lower or all upper values are treated as snake/kebab since they can't be pascal or camel
    if (
      SNAKE_KEBAB_PATTERN.test(output) ||
      output === output.toLocaleUpperCase() ||
      output === output.toLocaleLowerCase()
    ) {
      output = output.toLocaleLowerCase().replace(SNAKE_KEBAB_PATTERN, ' ');
    }

    // convert pascal, camel, snake, and kebab case to space-separated
    if (CAPITAL_LETTER_PATTERN.test(output)) {
      output = output.split(PASCAL_PATTERN).join(' ');
    } else {
      output = output.replace(CAMEL_PATTERN, '$1 $2');
    }

    // reset to always search from start
    SNAKE_KEBAB_PATTERN.lastIndex = 0;
    CAPITAL_LETTER_PATTERN.lastIndex = 0;
  }

  switch (outputCase) {
    case 'camel':
    case 'pascal':
      output = output
        .split(' ')
        .map((word, index) => {
          const keepLower = outputCase === 'camel' && index === 0;
          return keepLower
            ? word.toLocaleLowerCase()
            : formatString(word, { outputCase: 'sentence' });
        })
        .join('');
      break;
    case 'kebab':
    case 'snake':
      output = output.toLocaleLowerCase().split(' ').join(JOINERS[outputCase]);
      break;
    case 'sentence':
      output = `${(output[0] || '').toLocaleUpperCase()}${output.substring(1)}`;
      break;
    case 'title':
      output = output
        .toLocaleLowerCase()
        .split(' ')
        .map((word, index) => {
          const keepLower = index > 0 && MINOR_WORDS.has(word);
          const acronym = ACRONYMS[word as AcronymKeys] ?? ACRONYMS.normalize(word);
          return keepLower ? word : acronym ?? formatString(word, { outputCase: 'sentence' });
        })
        .join(' ');
      break;
    case 'lower':
      output = output.toLocaleLowerCase();
      break;
    case 'upper':
      output = output.toLocaleUpperCase();
      break;
  }

  return output;
}

const LABEL_OPTIONS = { normalize: 'naming', outputCase: 'title' } as const;
const LABEL_OPTIONS_NO_NORMALIZE = { outputCase: 'title' } as const;

export function formatLabel(value: string | Nullish, shouldNormalize = true) {
  return formatString(value, shouldNormalize ? LABEL_OPTIONS : LABEL_OPTIONS_NO_NORMALIZE);
}
